import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Center, Container, Paper, Stack, Text } from '@mantine/core';
import { IconLogin, IconUserPlus } from '@tabler/icons-react';
import HuginnMark from './riskManager/components/HuginnMark';
import classes from './authPage.module.css';

/**
 * Sign in.
 *
 * This was the last Mantine v6 component in a v8 app, and three things on it were doing nothing
 * at all: `leftIcon` (v7 renamed it `leftSection`, so neither button icon had ever rendered),
 * `spacing` on Stack (now `gap`, so all three values were ignored and the lockup was using
 * defaults), and `styles={{ root: { '&:hover': … } }}` — v7 dropped Emotion, `styles` emits inline
 * styles, and `&:hover` is not a CSS property, so both hover states were dead.
 *
 * The colours were Bootstrap greys rather than the deck: #f8f9fa, #e9ecef, #212529, #adb5bd. They
 * are tokens now, and the buttons take their colour from the theme instead of declaring it —
 * `primaryColor: navy` with `primaryShade: 8` is #1F3352, the same value as the bar, which is the
 * whole point of that palette rule. The hard-coded -apple-system stack is gone too; the page
 * inherits Hanken Grotesk like everything else.
 *
 * A later pass took the elegance in one direction: less of everything. The halo lost its edge, the
 * name and tagline each came down a step so the mark leads, the second button was demoted to an
 * outline so there is one obvious default, and the Auth0 line went below a hairline. Nothing was
 * added. The reasoning for each sits at the point of change.
 */
const AuthPage = () => {
  const { loginWithRedirect } = useAuth0();

  const handleSignIn = () => {
    loginWithRedirect();
  };

  const handleSignUp = () => {
    loginWithRedirect({ screen_hint: 'signup' });
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--rm-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container size="sm">
        <Center>
          {/* Padding is 56 / 60 / 44, not a uniform 60: the lockup is the heavier half of the
              card, so an even box puts its optical centre below the geometric one. */}
          <Paper
            radius={0}
            px={60}
            pt={56}
            pb={44}
            shadow="sm"
            style={{
              width: '100%',
              maxWidth: '480px',
              background: 'var(--rm-surface)',
              border: 'var(--rm-hairline)',
            }}
          >
            <Stack align="center" gap={34}>
              {/* One vertical lockup: mark, name, then what the product is.
                  "Welcome to Huginn" is gone — a greeting tells a visitor nothing, and the line
                  underneath has to do the work of saying what this is. */}
              <Stack align="center" gap={0}>
                {/* The mark settles first, then breathes; the words follow half a second later.
                    Motion lives in authPage.module.css, addressed through the marks' `data-beak`
                    attributes — HuginnMark itself stays inert, because it is also the app bar. */}
                <Box
                  className={classes.stage}
                  // Back to 16. The 36 was compensation for a hard-edged disc that visibly
                  // overflowed the 56px box and ate the gap; the halo now fades to nothing well
                  // before its own edge, so the measured gap is the gap you see.
                  style={{ color: 'var(--rm-navy)', width: 56, height: 56, marginBottom: 16 }}
                >
                  <HuginnMark size={56} className={classes.mark} />
                </Box>

                {/* 30, not 34. At 34 the name matched the mark for weight and the two competed;
                    the mark should be the first thing read and the name the second. */}
                <Text
                  className={classes.wordmark}
                  style={{
                    fontSize: 30,
                    fontWeight: 600,
                    letterSpacing: '-0.5px',
                    color: 'var(--rm-navy)',
                    lineHeight: 1.1,
                  }}
                >
                  Huginn
                </Text>

                {/* 280px, so the line breaks after "for the" and the tagline is two even lines
                    under the name rather than one wide block wider than the lockup. */}
                <Text
                  className={classes.tagline}
                  style={{
                    textAlign: 'center',
                    maxWidth: '280px',
                    color: 'var(--rm-navy-70)',
                    fontSize: 14.5,
                    lineHeight: 1.55,
                    marginTop: 9,
                  }}
                >
                  Risk management for the agentic workforce
                </Text>
              </Stack>

              {/* One default and one alternative, not two equal choices — see `.secondary` in the
                  stylesheet. "Create an account" rather than "Sign Up", because next to "Sign in"
                  the two verbs are a letter apart and you have to read them to tell them apart. */}
              <Stack gap={10} style={{ width: '100%', maxWidth: '300px' }}>
                <Button
                  size="md"
                  radius={0}
                  h={46}
                  fullWidth
                  leftSection={<IconLogin size={17} />}
                  onClick={handleSignIn}
                >
                  Sign in
                </Button>

                <Button
                  size="md"
                  radius={0}
                  h={46}
                  fullWidth
                  variant="default"
                  className={classes.secondary}
                  leftSection={<IconUserPlus size={17} />}
                  onClick={handleSignUp}
                >
                  Create an account
                </Button>
              </Stack>

              {/* Below a hairline rather than floating under the buttons: it is a footnote about
                  the page, not a third thing to consider. */}
              <Stack align="center" gap={14} style={{ width: '100%' }}>
                <Box style={{ width: '100%', height: 1, background: 'var(--rm-line-soft)' }} />
                <Text size="xs" ta="center" style={{ color: 'var(--rm-navy-40)' }}>
                  Secure authentication by Auth0
                </Text>
              </Stack>
            </Stack>
          </Paper>
        </Center>
      </Container>
    </Box>
  );
};

export default AuthPage;
